import type { Sprint } from '../sprints/sprintsApi';

interface SprintVelocity {
  sprint: Sprint;
  completedPoints: number;
  totalPoints: number;
}

interface VelocityChartProps {
  data: SprintVelocity[];
}

export default function VelocityChart({ data }: VelocityChartProps) {
  const closedSprints = data.filter(d => d.sprint.status === 'CLOSED');
  if (closedSprints.length === 0) return null;

  const maxPoints = Math.max(...closedSprints.map(d => d.totalPoints), 1);
  const W = Math.max(closedSprints.length * 60, 240);
  const H = 160;
  const PAD = { top: 10, right: 10, bottom: 40, left: 36 };
  const plotH = H - PAD.top - PAD.bottom;
  const barW = (W - PAD.left - PAD.right) / closedSprints.length;

  function yOf(pts: number) { return PAD.top + plotH - (pts / maxPoints) * plotH; }

  const avgVelocity = closedSprints.length > 0
    ? Math.round(closedSprints.reduce((s, d) => s + d.completedPoints, 0) / closedSprints.length)
    : 0;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h3 style={{ fontSize: 14, margin: '0 0 4px' }}>Velocity</h3>
        <span className="muted" style={{ fontSize: 12 }}>Avg: {avgVelocity} pts/sprint</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <svg width={W} height={H} style={{ display: 'block' }}>
          {/* Axes */}
          <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + plotH} stroke="var(--color-border)" />
          <line x1={PAD.left} y1={PAD.top + plotH} x2={PAD.left + (W - PAD.left - PAD.right)} y2={PAD.top + plotH} stroke="var(--color-border)" />
          {/* Y labels */}
          <text x={PAD.left - 4} y={PAD.top + 4} fontSize={9} fill="var(--color-text-muted)" textAnchor="end">{maxPoints}</text>
          <text x={PAD.left - 4} y={PAD.top + plotH} fontSize={9} fill="var(--color-text-muted)" textAnchor="end">0</text>

          {closedSprints.map((d, i) => {
            const x = PAD.left + i * barW;
            const completedH = (d.completedPoints / maxPoints) * plotH;
            const totalH = (d.totalPoints / maxPoints) * plotH;
            return (
              <g key={d.sprint.sprintId}>
                {/* Total points (light) */}
                <rect x={x + 6} y={yOf(d.totalPoints)} width={barW - 12} height={totalH} fill="#bfdbfe" rx={2} />
                {/* Completed points (solid) */}
                <rect x={x + 6} y={yOf(d.completedPoints)} width={barW - 12} height={completedH} fill="#3b82f6" rx={2} />
                {/* Sprint label */}
                <text
                  x={x + barW / 2}
                  y={H - 4}
                  fontSize={9}
                  fill="var(--color-text-muted)"
                  textAnchor="middle"
                  transform={`rotate(-30, ${x + barW / 2}, ${H - 4})`}
                >
                  {d.sprint.name.slice(0, 8)}
                </text>
                {/* Points label */}
                <text x={x + barW / 2} y={yOf(d.completedPoints) - 2} fontSize={9} fill="var(--color-text)" textAnchor="middle">
                  {d.completedPoints}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#3b82f6', borderRadius: 2, verticalAlign: 'middle', marginRight: 4 }} />Completed</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#bfdbfe', borderRadius: 2, verticalAlign: 'middle', marginRight: 4 }} />Total planned</span>
      </div>
    </div>
  );
}
